// chess.com keeps one archive per month, listed oldest first. The import
// reads the months that hold the last 30 days, one click at a time. Only two
// endpoints are ever called, the archives list and a month, and as rarely as
// possible: everything fetched is kept as fetched under a path keyed by
// website and account, a past month is fetched once after it ended, the live
// month at most once a minute, and the archives
// list only when it could have gained a month and was not checked within
// the hour. Every call is logged with the reason the cache did not serve
// it. The games API only ever receives finished records; this bookkeeping
// is the importer's own. Pure over `fetch` and `Storage`, so it runs under
// Node.
import type { Storage } from '@chess/api-storage';
import { archivesUrl, monthOf, type ChesscomGame } from './chesscom.ts';

/** How long a stored archives list that ends before the current month is trusted. */
export const ARCHIVES_CHECK_INTERVAL_MS = 60 * 60 * 1000;
/** How long a fresh copy of the live month is trusted: the double-click guard. */
export const LIVE_MONTH_INTERVAL_MS = 60 * 1000;
/** How long after an import click the next one is refused. */
export const IMPORT_CLICK_INTERVAL_MS = 60 * 1000;
/** After this long since the last import, a load or a return to the tab reads chess.com again for new games (2026-10-14). */
export const REFRESH_INTERVAL_MS = 10 * 60 * 1000;

/** The importer's bookkeeping: `state.json`. */
export interface ImportState {
  schemaVersion: 4;
  /** Archive URLs whose games are in the library. */
  months: string[];
  /** When the archives endpoint was last called, ISO 8601. */
  archivesCheckedAt?: string;
  /** The live month's last fetch, ISO 8601. */
  liveMonthFetchedAt?: string;
  /** The last import click, ISO 8601; the next within a minute is refused. */
  importClickedAt?: string;
  /** Past months fetched after they ended, so their stored response is final and never fetched again. */
  closedMonths?: string[];
  /** The local day the last sync's import completed (`localDay` in `@chess/api-sync`); the daily sync is due while it is not today. */
  lastSyncDay?: string;
  /** When the last import completed, ISO 8601: a load or a return to the tab more than REFRESH_INTERVAL_MS later reads chess.com again. */
  lastImportAt?: string;
}

export const EMPTY_STATE: ImportState = { schemaVersion: 4, months: [] };

export function parseState(text: string | undefined): ImportState {
  if (!text) return EMPTY_STATE;
  try {
    const raw = JSON.parse(text) as Partial<ImportState>;
    if (raw.schemaVersion !== 4 || !Array.isArray(raw.months)) return EMPTY_STATE;
    return {
      schemaVersion: 4,
      months: raw.months.filter((m): m is string => typeof m === 'string'),
      archivesCheckedAt: typeof raw.archivesCheckedAt === 'string' ? raw.archivesCheckedAt : undefined,
      liveMonthFetchedAt: typeof raw.liveMonthFetchedAt === 'string' ? raw.liveMonthFetchedAt : undefined,
      importClickedAt: typeof raw.importClickedAt === 'string' ? raw.importClickedAt : undefined,
      closedMonths: Array.isArray(raw.closedMonths) ? raw.closedMonths.filter((m): m is string => typeof m === 'string') : undefined,
      lastSyncDay: typeof raw.lastSyncDay === 'string' ? raw.lastSyncDay : undefined,
      lastImportAt: typeof raw.lastImportAt === 'string' ? raw.lastImportAt : undefined,
    };
  } catch {
    return EMPTY_STATE;
  }
}

/** "2026/10" for a moment in time, UTC, the way chess.com names archives. */
export function currentMonth(now: Date): string {
  return `${now.getUTCFullYear()}/${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** "September 2026" for an archive URL. */
export function monthLabel(archiveUrl: string): string {
  const [year, month] = monthOf(archiveUrl).split('/').map(Number);
  const name = new Date(Date.UTC(year, month - 1, 1)).toLocaleString('en-US', { month: 'long', timeZone: 'UTC' });
  return `${name} ${year}`;
}

/** The archives holding games of the window that starts at `start` (ms): its first calendar month on, oldest first. */
export function monthsSince(archives: string[], start: number): string[] {
  const first = currentMonth(new Date(start));
  return archives.filter((url) => monthOf(url) >= first);
}

/** Milliseconds since an ISO timestamp; Infinity when there is none or it is unreadable. */
export function ageMs(iso: string | undefined, now: Date): number {
  if (!iso) return Infinity;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? now.getTime() - t : Infinity;
}

/** What is logged for every call to chess.com. */
export type CallLog = (endpoint: string, reason: string) => void;

export const consoleLog: CallLog = (endpoint, reason) => console.debug(`[import-chesscom] GET ${endpoint}: ${reason}`);

export class RateLimited extends Error {
  constructor(url: string) {
    super(`chess.com is rate limiting requests (429 for ${url}). Wait a minute, then click again.`);
    this.name = 'RateLimited';
  }
}

/** Plain fetch, default headers only: the API's preflight allows nothing else. */
async function getJson<T>(fetchFn: typeof fetch, url: string, log: CallLog, reason: string): Promise<T> {
  log(url, reason);
  const res = await fetchFn(url);
  if (res.status === 429) throw new RateLimited(url);
  if (!res.ok) throw new Error(`chess.com answered ${res.status} for ${url}`);
  return (await res.json()) as T;
}

/**
 * Everything kept for one account, under `<account>/` inside the plugin's
 * website-keyed folder: the archives list and each month as chess.com sent
 * them, and the bookkeeping beside them.
 */
export class ArchiveStore {
  private readonly storage: Storage;

  constructor(storage: Storage, username: string) {
    this.storage = storage.scope(username.toLowerCase().replace(/[^a-z0-9_-]/g, '_'));
  }

  async readArchives(): Promise<string[] | undefined> {
    const text = await this.storage.readText('archives.json');
    if (!text) return undefined;
    try {
      const raw = JSON.parse(text) as { archives?: unknown };
      return Array.isArray(raw.archives) ? raw.archives.filter((a): a is string => typeof a === 'string') : undefined;
    } catch {
      return undefined;
    }
  }

  writeArchives(raw: unknown): Promise<void> {
    return this.storage.write('archives.json', JSON.stringify(raw));
  }

  private monthFile(url: string): string {
    return `months/${monthOf(url).replace('/', '-')}.json`;
  }

  async readMonth(url: string): Promise<ChesscomGame[] | undefined> {
    const text = await this.storage.readText(this.monthFile(url));
    if (!text) return undefined;
    try {
      const raw = JSON.parse(text) as { games?: unknown };
      return Array.isArray(raw.games) ? (raw.games as ChesscomGame[]) : undefined;
    } catch {
      return undefined;
    }
  }

  writeMonth(url: string, raw: unknown): Promise<void> {
    return this.storage.write(this.monthFile(url), JSON.stringify(raw));
  }

  async readState(): Promise<ImportState> {
    return parseState(await this.storage.readText('state.json'));
  }

  writeState(state: ImportState): Promise<void> {
    return this.storage.write('state.json', JSON.stringify(state));
  }
}

export interface LoadArchivesOptions {
  store: ArchiveStore;
  fetch: typeof fetch;
  username: string;
  now: Date;
  log: CallLog;
  /** False to read only the stored list, never calling the archives endpoint. */
  mayFetch: boolean;
}

/**
 * The account's monthly archives, oldest first. The stored list is enough
 * when it ends at the current month, since no newer archive can exist yet,
 * or when it was checked within the hour. Otherwise chess.com is asked, the
 * answer kept as sent and the check noted.
 */
export async function loadArchives(options: LoadArchivesOptions): Promise<string[] | undefined> {
  const { store, now } = options;
  const stored = await store.readArchives();
  if (stored && stored.length > 0) {
    if (monthOf(stored[stored.length - 1]) === currentMonth(now)) return stored;
    const state = await store.readState();
    if (ageMs(state.archivesCheckedAt, now) < ARCHIVES_CHECK_INTERVAL_MS) return stored;
  }
  if (!options.mayFetch) return stored;
  const reason = stored && stored.length > 0 ? 'stored list ends before the current month and was last checked over an hour ago' : 'no archives list stored';
  const raw = await getJson<{ archives: string[] }>(options.fetch, archivesUrl(options.username), options.log, reason);
  await store.writeArchives(raw);
  const state = await store.readState();
  await store.writeState({ ...state, archivesCheckedAt: now.toISOString() });
  return raw.archives;
}

/** A month's games newest first. */
export function newestFirst(games: ChesscomGame[]): ChesscomGame[] {
  return [...games].sort((a, b) => (b.end_time ?? 0) - (a.end_time ?? 0));
}

export interface LoadMonthOptions {
  store: ArchiveStore;
  fetch: typeof fetch;
  url: string;
  log: CallLog;
  /** Fetch even when a copy is stored: the live month, or a past month stored before it ended. */
  fresh?: boolean;
}

/**
 * A month's games, from the stored response when there is one; only a
 * `fresh` request (the live month, or a past month first read while live)
 * goes to chess.com again.
 */
export async function loadMonth(options: LoadMonthOptions): Promise<ChesscomGame[]> {
  const { store, url } = options;
  const stored = await store.readMonth(url);
  if (stored && !options.fresh) return newestFirst(stored);
  const reason = stored ? 'the month may have grown since it was stored' : 'month not stored yet';
  const raw = await getJson<{ games: ChesscomGame[] }>(options.fetch, url, options.log, reason);
  await store.writeMonth(url, raw);
  return newestFirst(raw.games);
}

/** The saved chess.com username, or '' when none is set or the file is unreadable. */
export function readUsername(text: string | undefined): string {
  if (!text) return '';
  try {
    const raw = JSON.parse(text) as { schemaVersion?: number; username?: unknown };
    return raw.schemaVersion === 1 && typeof raw.username === 'string' ? raw.username.trim() : '';
  } catch {
    return '';
  }
}
